import { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import { MODULE_VERTRIEB_COLOR } from '@/modules/moduleConstants'

// Vertrieb
const AngeboteList = () => import('@/modules/vertrieb/AngeboteList.vue')
const AngeboteForm = () => import('@/modules/vertrieb/AngeboteForm.vue')
const VertraegeList = () => import('@/modules/vertrieb/VertraegeList.vue')
const StaffelausgleichList = () => import('@/modules/vertrieb/StaffelausgleichList.vue')
const VertraegeForm = () => import('@/modules/vertrieb/VertraegeForm.vue')
const ProdukteList = () => import('@/modules/vertrieb/ProdukteList.vue')
const ProdukteForm = () => import('@/modules/vertrieb/ProdukteForm.vue')
const MasterInstanzenList = () => import('@/modules/vertrieb/MasterInstanzenList.vue')
const MasterInstanzenForm = () => import('@/modules/vertrieb/MasterInstanzenForm.vue')
const VertriebspartnerList = () => import('@/modules/vertrieb/VertriebspartnerList.vue')
const VertriebspartnerForm = () => import('@/modules/vertrieb/VertriebspartnerForm.vue')
const PreislistenList = () => import('@/modules/vertrieb/PreislistenList.vue')
const PreislistenForm = () => import('@/modules/vertrieb/PreislistenForm.vue')
const PreislistenProdukteForm = () => import('@/modules/vertrieb/PreislistenProdukteForm.vue')
const PreisstaffelnForm = () => import('@/modules/vertrieb/PreisstaffelnForm.vue')
const PreislaeufeList = () => import('@/modules/vertrieb/PreislaeufeList.vue')
const PreislaeufeForm = () => import('@/modules/vertrieb/PreislaeufeForm.vue')
// Liste und Kartenansicht kommen unverändert aus `organisation`; nur die Maske ist eine eigene —
// sie füllt den Slot der Organisation-Maske mit den Vertriebs-Karten.
const KundenList = () => import('@/modules/organisation/KundenList.vue')
const KundenMap = () => import('@/modules/organisation/KundenMap.vue')
const KundenForm = () => import('@/modules/vertrieb/KundenForm.vue')

const updateFormProps = (route: RouteLocationNormalized) => ({
  id: parseInt(route.params.id as string)
})

const routes: RouteRecordRaw[] = [
  // Angebote
  {
    path: '/vertrieb/angebote/',
    name: 'AngeboteList',
    component: AngeboteList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  // Erstangebot erfassen; das Änderungsangebot kommt mit Schritt 7 und trägt den Vertragsbezug.
  {
    path: '/vertrieb/angebote/add',
    name: 'AngeboteForm',
    component: AngeboteForm,
    meta: { icon: 'mdi-file-document-plus-outline', color: MODULE_VERTRIEB_COLOR }
  },
  // **Eine** Maske je Angebot (#13830): erfassen, bearbeiten, lesen, annehmen. Gesperrt wird nach
  // dem Status - `offen` und `vorgemerkt` sind veränderlich, der Rest endgültig (ADR 0008).
  // Deshalb keine zweite, read-only Adresse daneben; auch der Auflösungsweg „Anpassen" (#13676)
  // ist ein Zustand dieser Maske und keine eigene Route. Nach `/add`, sonst fängt `:id` das ab.
  {
    path: '/vertrieb/angebote/:id',
    name: 'AngeboteFormUpdate',
    component: AngeboteForm,
    meta: { icon: 'mdi-file-document-outline', color: MODULE_VERTRIEB_COLOR }
  },
  // Verträge
  {
    path: '/vertrieb/vertraege/',
    name: 'VertraegeList',
    component: VertraegeList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  // Read-only: jede finanzwirksame Änderung läuft über Angebot + Rebase (ADR 0008), deshalb
  // kein Form-Pendant unter derselben Adresse.
  {
    path: '/vertrieb/vertraege/:id',
    name: 'VertraegeFormUpdate',
    component: VertraegeForm,
    meta: { icon: 'mdi-file-document-outline', color: MODULE_VERTRIEB_COLOR }
  },
  // Produkte — der Katalog (asix/organisation#13618). Eine Maske für erfassen und ändern.
  {
    path: '/vertrieb/produkte/',
    name: 'ProdukteList',
    component: ProdukteList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  // Vor `:id`, sonst fängt das `add` ab.
  {
    path: '/vertrieb/produkte/add',
    name: 'ProdukteForm',
    component: ProdukteForm,
    meta: { icon: 'mdi-package-variant-closed', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/produkte/:id',
    name: 'ProduktFormUpdate',
    component: ProdukteForm,
    meta: { icon: 'mdi-package-variant-closed', color: MODULE_VERTRIEB_COLOR }
  },
  // Master-Instanzen — der Instanz-Steckbrief braucht sie als Stammdaten
  // (asix/organisation#14379, ADR 0023 §4). Eine Maske für erfassen und ändern, wie beim Produkt.
  {
    path: '/vertrieb/masterinstanzen/',
    name: 'MasterInstanzenList',
    component: MasterInstanzenList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  // Vor `:id`, sonst fängt das `add` ab.
  {
    path: '/vertrieb/masterinstanzen/add',
    name: 'MasterInstanzenForm',
    component: MasterInstanzenForm,
    meta: { icon: 'mdi-server-network', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/masterinstanzen/:id',
    name: 'MasterInstanzFormUpdate',
    component: MasterInstanzenForm,
    meta: { icon: 'mdi-server-network', color: MODULE_VERTRIEB_COLOR }
  },
  // Vertriebspartner — die Firmen hinter Berater und Leadgeber (asix/organisation#14688,
  // ADR 0017). Eine Maske für erfassen und ändern, wie bei den Master-Instanzen.
  {
    path: '/vertrieb/vertriebspartner/',
    name: 'VertriebspartnerList',
    component: VertriebspartnerList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  // Vor `:id`, sonst fängt das `add` ab.
  {
    path: '/vertrieb/vertriebspartner/add',
    name: 'VertriebspartnerForm',
    component: VertriebspartnerForm,
    meta: { icon: 'mdi-handshake-outline', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/vertriebspartner/:id',
    name: 'VertriebspartnerFormUpdate',
    component: VertriebspartnerForm,
    meta: { icon: 'mdi-handshake-outline', color: MODULE_VERTRIEB_COLOR }
  },
  // Preislisten — die Pflegemaske samt Freigabe (asix/organisation#13625, ADR 0006).
  //
  // **Drei Ebenen, drei Masken** statt einer mit verschachtelten Grids: an einem Listeneintrag
  // von `Endkunde 2026` hängen rund 240 Staffelzeilen.
  {
    path: '/vertrieb/preislisten/',
    name: 'PreislistenList',
    component: PreislistenList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  // Vor `:id`, sonst fängt das `add` ab.
  {
    path: '/vertrieb/preislisten/add',
    name: 'PreislistenForm',
    component: PreislistenForm,
    meta: { icon: 'mdi-cash-multiple', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/preislisten/:id',
    name: 'PreislisteFormUpdate',
    component: PreislistenForm,
    meta: { icon: 'mdi-cash-multiple', color: MODULE_VERTRIEB_COLOR }
  },
  // Die Listeneinträge: keine eigene Liste, sie hängen als Karte an der Preisliste. Erreichbar
  // ist die Maske über den Stift dieser Karte und über `?preisliste=<id>` beim Anlegen.
  {
    path: '/vertrieb/preislistenprodukte/add',
    name: 'PreislistenProdukteForm',
    component: PreislistenProdukteForm,
    meta: { icon: 'mdi-package-variant-closed', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/preislistenprodukte/:id',
    name: 'PreislistenProduktFormUpdate',
    component: PreislistenProdukteForm,
    meta: { icon: 'mdi-package-variant-closed', color: MODULE_VERTRIEB_COLOR }
  },
  // Dasselbe eine Ebene tiefer: die Staffeln hängen als Karte am Listeneintrag.
  {
    path: '/vertrieb/preisstaffeln/add',
    name: 'PreisstaffelnForm',
    component: PreisstaffelnForm,
    meta: { icon: 'mdi-table', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/preisstaffeln/:id',
    name: 'PreisstaffelFormUpdate',
    component: PreisstaffelnForm,
    meta: { icon: 'mdi-table', color: MODULE_VERTRIEB_COLOR }
  },
  // Preisrunden — der Massenlauf samt Postenliste und Warteschlange
  // (asix/organisation#13481, ADR 0010).
  //
  // **Keine `/add`-Route:** ein Lauf entsteht über den Auslösungs-Dialog der Liste, weil sein
  // Anlegen und das Erfassen des Bestands ein Vorgang sind (ADR 0010 §7). Eine Erfassungsmaske
  // daneben hätte einen Lauf ohne Posten zurückgelassen — und den gibt es nicht wieder los, der
  // Endpunkt kennt bewusst keinen Löschweg (§1).
  // Der Bericht zum Staffelausgleich (asix/organisation#14067) — die Vertragsliste mit eigener
  // Ansicht, erreichbar aus dem Menü und aus dem Staffelwechsel-Lauf.
  {
    path: '/vertrieb/staffelausgleich/',
    name: 'StaffelausgleichList',
    component: StaffelausgleichList,
    meta: { icon: 'mdi-scale-balance', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/preislaeufe/',
    name: 'PreislaeufeList',
    component: PreislaeufeList,
    meta: { icon: 'mdi-view-list', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/preislaeufe/:id',
    name: 'PreislaeufeFormUpdate',
    component: PreislaeufeForm,
    meta: { icon: 'mdi-cash-sync', color: MODULE_VERTRIEB_COLOR }
  },
  // Kunden — derselbe Bestand wie unter `/organisation/kunden/`, mit zwei Karten an der Maske.
  {
    path: '/vertrieb/kunden/',
    name: 'VertriebKundenList',
    component: KundenList,
    meta: { icon: 'mdi-account-tie-outline', color: MODULE_VERTRIEB_COLOR }
  },
  // `add` und `map` rechnen pfadrelativ (`BaseLayoutList`) und liefen ohne eigene Route ins
  // Leere. Vor `:id`, sonst fängt das die beiden ab.
  {
    path: '/vertrieb/kunden/add',
    name: 'VertriebKundenForm',
    component: KundenForm,
    meta: { icon: 'mdi-account-tie-outline', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/kunden/map',
    name: 'VertriebKundenMap',
    component: KundenMap,
    meta: { icon: 'mdi-account-tie-outline', color: MODULE_VERTRIEB_COLOR }
  },
  {
    path: '/vertrieb/kunden/:id',
    name: 'VertriebKundenFormUpdate',
    component: KundenForm,
    props: updateFormProps,
    meta: { icon: 'mdi-account-tie-outline', color: MODULE_VERTRIEB_COLOR }
  },
]

export default routes
